import { useState, useMemo } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

function Row({ label, value, big }) {
  return (
    <div className="flex items-baseline justify-between py-3 border-b hairline last:border-b-0">
      <span className="font-mono-fine text-[11px] tracking-[0.2em] uppercase text-[var(--pta-ink)]/60">{label}</span>
      <span className={`font-mono-fine ${big ? "text-2xl md:text-3xl text-[var(--pta-navy)]" : "text-lg text-[var(--pta-ink)]"}`}>{value}</span>
    </div>
  );
}

// Simplified FY2025-26 slabs
function calcOld(income, age = "below60") {
  let taxable = Math.max(0, income - 50000);
  let slabExempt = age === "80plus" ? 500000 : age === "60to80" ? 300000 : 250000;
  let tax = 0;
  const s = [
    [slabExempt, 0],
    [500000, 0.05],
    [1000000, 0.20],
    [Infinity, 0.30],
  ];
  let prev = 0;
  for (const [upto, rate] of s) {
    const bracket = Math.max(0, Math.min(taxable, upto) - prev);
    tax += bracket * rate;
    prev = upto;
    if (taxable <= upto) break;
  }
  return tax * 1.04;
}
function calcNew(income) {
  let taxable = Math.max(0, income - 75000);
  const s = [
    [400000, 0], [800000, 0.05], [1200000, 0.10], [1600000, 0.15],
    [2000000, 0.20], [2400000, 0.25], [Infinity, 0.30],
  ];
  let tax = 0, prev = 0;
  for (const [upto, rate] of s) {
    const bracket = Math.max(0, Math.min(taxable, upto) - prev);
    tax += bracket * rate;
    prev = upto;
    if (taxable <= upto) break;
  }
  if (taxable <= 700000) tax = Math.max(0, tax - 25000);
  return tax * 1.04;
}

function IncomeTaxCalc() {
  const [income, setIncome] = useState(1500000);
  const [age, setAge] = useState("below60");
  const oldT = calcOld(income, age), newT = calcNew(income);
  const better = oldT < newT ? "OLD" : "NEW";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="space-y-6">
        <div>
          <Label className="overline">Annual Income (₹)</Label>
          <Input data-testid="calc-income-input" type="number" value={income} onChange={(e)=>setIncome(+e.target.value)} className="mt-2 font-mono-fine text-xl" />
          <Slider data-testid="calc-income-slider" value={[income]} onValueChange={(v)=>setIncome(v[0])} min={100000} max={10000000} step={50000} className="mt-4" />
        </div>
        <div>
          <Label className="overline">Age</Label>
          <Select value={age} onValueChange={setAge}>
            <SelectTrigger data-testid="calc-age" className="mt-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="below60">Below 60</SelectItem>
              <SelectItem value="60to80">Senior 60–80</SelectItem>
              <SelectItem value="80plus">Super Senior 80+</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="border hairline-navy p-6 bg-[var(--pta-paper)]">
        <div className="chapter-num">Estimate · FY 2025-26</div>
        <Row label="Old Regime Tax" value={inr(oldT)} big />
        <Row label="New Regime Tax" value={inr(newT)} big />
        <div className="mt-6 pt-6 border-t hairline-gold flex items-baseline justify-between">
          <span className="overline">Recommended</span>
          <span className="font-serif-display text-3xl text-[var(--pta-gold)]">{better} regime</span>
        </div>
        <p className="mt-4 text-xs text-[var(--pta-ink)]/60">Indicative only. Actual liability depends on deductions, capital gains, surcharges, and cess. Consult us for a precise plan.</p>
      </div>
    </div>
  );
}

function GstCalc() {
  const [amt, setAmt] = useState(10000);
  const [rate, setRate] = useState("18");
  const [type, setType] = useState("exclusive");
  const r = +rate / 100;
  const gst = type === "exclusive" ? amt * r : amt - amt / (1 + r);
  const total = type === "exclusive" ? amt + gst : amt;
  const base = type === "exclusive" ? amt : amt - gst;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="space-y-5">
        <div><Label className="overline">Amount (₹)</Label><Input data-testid="gst-amt" type="number" value={amt} onChange={(e)=>setAmt(+e.target.value)} className="mt-2 font-mono-fine text-xl" /></div>
        <div><Label className="overline">GST Rate</Label>
          <Select value={rate} onValueChange={setRate}><SelectTrigger data-testid="gst-rate" className="mt-2"><SelectValue /></SelectTrigger><SelectContent>{["0","5","12","18","28"].map(r=><SelectItem key={r} value={r}>{r}%</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label className="overline">Type</Label>
          <Select value={type} onValueChange={setType}><SelectTrigger data-testid="gst-type" className="mt-2"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="exclusive">Exclusive (add GST)</SelectItem><SelectItem value="inclusive">Inclusive (extract GST)</SelectItem></SelectContent></Select>
        </div>
      </div>
      <div className="border hairline-navy p-6 bg-[var(--pta-paper)]">
        <div className="chapter-num">Break-up</div>
        <Row label="Base amount" value={inr(base)} />
        <Row label="CGST" value={inr(gst/2)} />
        <Row label="SGST" value={inr(gst/2)} />
        <Row label="Total GST" value={inr(gst)} big />
        <Row label="Final amount" value={inr(total)} big />
      </div>
    </div>
  );
}

function EmiCalc() {
  const [p, setP] = useState(2500000);
  const [r, setR] = useState(9);
  const [n, setN] = useState(20);
  const monthlyRate = r/12/100, months = n*12;
  const emi = useMemo(()=> p * monthlyRate * Math.pow(1+monthlyRate, months) / (Math.pow(1+monthlyRate, months) - 1), [p,r,n]);
  const total = emi * months, interest = total - p;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="space-y-5">
        <div><Label className="overline">Loan (₹)</Label><Input data-testid="emi-p" type="number" value={p} onChange={(e)=>setP(+e.target.value)} className="mt-2 font-mono-fine text-xl" /><Slider value={[p]} onValueChange={(v)=>setP(v[0])} min={100000} max={20000000} step={50000} className="mt-3" /></div>
        <div><Label className="overline">Rate % p.a.</Label><Input data-testid="emi-r" type="number" step="0.1" value={r} onChange={(e)=>setR(+e.target.value)} className="mt-2 font-mono-fine text-xl" /><Slider value={[r]} onValueChange={(v)=>setR(v[0])} min={5} max={20} step={0.1} className="mt-3" /></div>
        <div><Label className="overline">Tenure (yrs)</Label><Input data-testid="emi-n" type="number" value={n} onChange={(e)=>setN(+e.target.value)} className="mt-2 font-mono-fine text-xl" /><Slider value={[n]} onValueChange={(v)=>setN(v[0])} min={1} max={30} step={1} className="mt-3" /></div>
      </div>
      <div className="border hairline-navy p-6 bg-[var(--pta-paper)]">
        <div className="chapter-num">Schedule</div>
        <Row label="Monthly EMI" value={inr(emi)} big />
        <Row label="Total interest" value={inr(interest)} />
        <Row label="Total payment" value={inr(total)} big />
      </div>
    </div>
  );
}

function SipCalc() {
  const [m, setM] = useState(10000);
  const [r, setR] = useState(12);
  const [n, setN] = useState(15);
  const i = r/12/100, months = n*12;
  const fv = m * ((Math.pow(1+i, months)-1)/i) * (1+i);
  const invested = m*months;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="space-y-5">
        <div><Label className="overline">Monthly SIP (₹)</Label><Input data-testid="sip-m" type="number" value={m} onChange={(e)=>setM(+e.target.value)} className="mt-2 font-mono-fine text-xl" /><Slider value={[m]} onValueChange={(v)=>setM(v[0])} min={500} max={200000} step={500} className="mt-3" /></div>
        <div><Label className="overline">Expected Return % p.a.</Label><Input data-testid="sip-r" type="number" value={r} onChange={(e)=>setR(+e.target.value)} className="mt-2 font-mono-fine text-xl" /><Slider value={[r]} onValueChange={(v)=>setR(v[0])} min={5} max={25} step={0.5} className="mt-3" /></div>
        <div><Label className="overline">Years</Label><Input data-testid="sip-n" type="number" value={n} onChange={(e)=>setN(+e.target.value)} className="mt-2 font-mono-fine text-xl" /><Slider value={[n]} onValueChange={(v)=>setN(v[0])} min={1} max={40} step={1} className="mt-3" /></div>
      </div>
      <div className="border hairline-navy p-6 bg-[var(--pta-paper)]">
        <div className="chapter-num">Wealth build-up</div>
        <Row label="Invested" value={inr(invested)} />
        <Row label="Gains" value={inr(fv - invested)} />
        <Row label="Corpus" value={inr(fv)} big />
      </div>
    </div>
  );
}

function HraCalc() {
  const [basic, setBasic] = useState(50000);
  const [hra, setHra] = useState(20000);
  const [rent, setRent] = useState(18000);
  const [metro, setMetro] = useState("metro");
  const cityPct = metro === "metro" ? 0.5 : 0.4;
  const exempt = Math.max(0, Math.min(hra, rent - 0.1*basic, cityPct*basic));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="space-y-5">
        <div><Label className="overline">Monthly Basic (₹)</Label><Input data-testid="hra-basic" type="number" value={basic} onChange={(e)=>setBasic(+e.target.value)} className="mt-2 font-mono-fine text-xl" /></div>
        <div><Label className="overline">Monthly HRA received</Label><Input data-testid="hra-hra" type="number" value={hra} onChange={(e)=>setHra(+e.target.value)} className="mt-2 font-mono-fine text-xl" /></div>
        <div><Label className="overline">Monthly Rent paid</Label><Input data-testid="hra-rent" type="number" value={rent} onChange={(e)=>setRent(+e.target.value)} className="mt-2 font-mono-fine text-xl" /></div>
        <div><Label className="overline">City</Label>
          <Select value={metro} onValueChange={setMetro}><SelectTrigger data-testid="hra-city" className="mt-2"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="metro">Metro</SelectItem><SelectItem value="nonmetro">Non-metro</SelectItem></SelectContent></Select>
        </div>
      </div>
      <div className="border hairline-navy p-6 bg-[var(--pta-paper)]">
        <div className="chapter-num">HRA Exemption</div>
        <Row label="Exempt / month" value={inr(exempt)} />
        <Row label="Exempt / year" value={inr(exempt*12)} big />
        <Row label="Taxable HRA / year" value={inr((hra - exempt)*12)} />
      </div>
    </div>
  );
}

function RegimeCalc() {
  const [income, setIncome] = useState(1200000);
  const oldT = calcOld(income), newT = calcNew(income);
  const save = Math.abs(oldT - newT);
  const winner = oldT < newT ? "OLD" : "NEW";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="space-y-6">
        <div><Label className="overline">Annual Taxable Income (₹)</Label><Input data-testid="regime-income" type="number" value={income} onChange={(e)=>setIncome(+e.target.value)} className="mt-2 font-mono-fine text-xl" /><Slider value={[income]} onValueChange={(v)=>setIncome(v[0])} min={300000} max={10000000} step={50000} className="mt-3" /></div>
      </div>
      <div className="border hairline-navy p-6 bg-[var(--pta-paper)]">
        <div className="chapter-num">Comparison</div>
        <Row label="Old Regime" value={inr(oldT)} big />
        <Row label="New Regime" value={inr(newT)} big />
        <div className="mt-6 pt-6 border-t hairline-gold">
          <div className="overline">Choose</div>
          <div className="font-serif-display text-4xl md:text-5xl text-[var(--pta-gold)] mt-2">{winner}</div>
          <div className="mt-2 text-sm text-[var(--pta-ink)]/70">Save approximately <span className="font-mono-fine">{inr(save)}</span></div>
        </div>
      </div>
    </div>
  );
}

export default function Calculators() {
  return (
    <section className="bg-[var(--pta-cream)] pt-16 md:pt-24 pb-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="chapter-num">Resources · Calculators</div>
        <h1 className="font-serif-display text-6xl md:text-8xl text-[var(--pta-navy)] mt-4 leading-[0.95]">Six calculators.<br /><em className="italic text-[var(--pta-gold)]">Zero fluff.</em></h1>
        <p className="mt-8 max-w-2xl text-[var(--pta-ink)]/75 text-lg">Quick indicative numbers. For actual filings and planning, book a session.</p>
        <div className="mt-14">
          <Tabs defaultValue="tax">
            <TabsList data-testid="calc-tabs" className="flex flex-wrap h-auto p-1 bg-[var(--pta-paper)] border hairline-navy">
              {[
                {v:"tax", l:"Income Tax"},
                {v:"gst", l:"GST"},
                {v:"emi", l:"EMI"},
                {v:"sip", l:"SIP"},
                {v:"hra", l:"HRA"},
                {v:"regime", l:"Old vs New"},
              ].map((t)=>(
                <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`} className="font-mono-fine text-[11px] tracking-[0.2em] uppercase data-[state=active]:bg-[var(--pta-navy)] data-[state=active]:text-[var(--pta-gold)]">{t.l}</TabsTrigger>
              ))}
            </TabsList>
            <div className="mt-10">
              <TabsContent value="tax"><IncomeTaxCalc /></TabsContent>
              <TabsContent value="gst"><GstCalc /></TabsContent>
              <TabsContent value="emi"><EmiCalc /></TabsContent>
              <TabsContent value="sip"><SipCalc /></TabsContent>
              <TabsContent value="hra"><HraCalc /></TabsContent>
              <TabsContent value="regime"><RegimeCalc /></TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </section>
  );
}
